import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';

export default function SystemSettings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get('/api/settings/system');
      setSettings(data.settings || []);
    } catch (err) {
      console.error('[SystemSettings] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings((prev) =>
      prev.map((s) => (s.key === key ? { ...s, value } : s))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      const values = {};
      settings.forEach((s) => {
        values[s.key] = s.value;
      });
      await api.put('/api/settings/system', { settings: values });
      setSaveMessage('Ajustes guardados correctamente.');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('[SystemSettings] Save failed:', err);
      setSaveMessage('Error al guardar los ajustes.');
    } finally {
      setSaving(false);
    }
  };

  const groupedSettings = () => {
    const groups = {};
    settings.forEach((s) => {
      const group = s.group || 'General';
      if (!groups[group]) groups[group] = [];
      groups[group].push(s);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando ajustes del sistema...</p>
      </div>
    );
  }

  const groups = groupedSettings();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Ajustes del Sistema</h1>
          <p className="text-sm text-dark-400 mt-1">Configura los ajustes generales de la aplicación</p>
        </div>
        <Link to="/settings" className="btn btn-secondary btn-sm">
          Volver a Ajustes
        </Link>
      </div>

      {saveMessage && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            saveMessage.includes('success')
              ? 'bg-success-900/30 border-success-700 text-success-400'
              : 'bg-error-900/30 border-error-700 text-error-400'
          }`}
        >
          {saveMessage}
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(groups).map(([groupName, groupSettings]) => (
          <div key={groupName} className="rounded-xl bg-dark-850 border border-dark-700 p-6">
            <h2 className="text-lg font-semibold text-dark-100 mb-4">{groupName}</h2>
            <div className="space-y-4">
              {groupSettings.map((setting) => (
                <div key={setting.key}>
                  <label className="block text-sm font-medium text-dark-300 mb-1">{setting.label}</label>
                  {setting.description && (
                    <p className="text-xs text-dark-500 mb-2">{setting.description}</p>
                  )}

                  {setting.type === 'checkbox' ? (
                    <div className="mt-2 flex items-center">
                      <input
                        type="checkbox"
                        checked={setting.value === true || setting.value === 'true'}
                        onChange={(e) => handleChange(setting.key, e.target.checked)}
                        className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
                      />
                      <span className="ml-2 text-sm text-dark-300">{setting.label}</span>
                    </div>
                  ) : setting.type === 'select' ? (
                    <select
                      value={setting.value || ''}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                      className="input"
                    >
                      {(setting.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : setting.type === 'number' ? (
                    <input
                      type="number"
                      value={setting.value || ''}
                      onChange={(e) =>
                        handleChange(
                          setting.key,
                          e.target.value ? parseInt(e.target.value, 10) : null
                        )
                      }
                      className="input"
                      min={setting.min}
                      max={setting.max}
                    />
                  ) : setting.type === 'textarea' ? (
                    <textarea
                      value={setting.value || ''}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                      className="input min-h-[80px]"
                    />
                  ) : setting.type === 'color' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={setting.value || '#000000'}
                        onChange={(e) => handleChange(setting.key, e.target.value)}
                        className="h-8 w-8 cursor-pointer rounded border border-dark-700 bg-transparent p-0"
                      />
                      <input
                        type="text"
                        value={setting.value || ''}
                        onChange={(e) => handleChange(setting.key, e.target.value)}
                        className="input w-24 text-sm"
                      />
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={setting.value || ''}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                      className="input"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? 'Guardando...' : 'Guardar Ajustes'}
        </button>
      </div>
    </div>
  );
}
